import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    MDBNavbar,
    MDBNavbarBrand,
    MDBIcon,
    MDBNavbarNav,
    MDBNavbarItem,
    MDBSwitch,
} from 'mdb-react-ui-kit';
import { AppContext, NavState } from "../App";
import "./TopNav.css";
import { Project, ProjectList } from 'konect-model';
import ProjectService from '../../services/project-service';
import UserControlService from '../../services/user-control-service';
import UserService from '../../services/user-service';
import UserSettings from './UserSettings';
import { Settings } from '../Settings';
import { createPortal } from 'react-dom';
import { GlyphEventService, GlyphEvent, NavEventService, TaskEditorEventService } from '../../services/event-service';
import { TaskEditorEvent } from './TaskEditorPreview';
import { NavEvent } from './SideNav';
import { useNavigate } from 'react-router-dom';

interface TopNavProps {
    onLoad: () => void;
    setShowProjectDialog: (state: boolean) => void;
    loginView: boolean;
    navState: NavState;
}

export default function TopNav(props: TopNavProps) {

    const { project, projectList, changeActiveProject, user, setUser } = useContext(AppContext);
    const navigate = useNavigate();

    const [saveAvailable, setSaveAvailable] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [undoAvailable, setUndoAvailable] = useState<boolean>(false);
    const [redoAvailable, setRedoAvailable] = useState<boolean>(false);

    const [showUserSettings, setShowUserSettings] = useState<boolean>(false);


    useEffect(() => {
        UserControlService.onUndoAvailable(
            (isAvailable: boolean) => {
                setUndoAvailable(isAvailable);
            });
        UserControlService.onRedoAvailable(
            (isAvailable: boolean) => {
                setRedoAvailable(isAvailable);
            });
        UserControlService.onSaveAvailable(
            (isAvailable: boolean) => {
                setSaveAvailable(isAvailable);
            });
        UserControlService.onSaving(
            (isSaving: boolean) => {
                setIsSaving(isSaving);
            });

    }, [])

    function save() {
        if (!saveAvailable) { return; }
        UserControlService.save();
    }

    function undo() {
        UserControlService.undo();
    }

    function redo() {
        UserControlService.redo();
    }


    // file menu dropdown functions
    async function importProject(event: any) {
        const input = event.target
        if (!('files' in input) || input.files.length <= 0) {
            return;
        }

        const p = await ProjectService.importProject(input.files[0], user);
        if (!p) {
            UserControlService.showInfoPopup({ title: "Import Error", content: `Could not import the project!`, duration: 5000, color: "danger" });
            return
        }
        ProjectList.addProject(projectList, p);
        changeActiveProject(p);
        setSaveAvailable(true);
        UserControlService.showInfoPopup({ title: "Import Successful", content: `Successfully imported project ${p.name}!`, duration: 5000 });
    }

    const logoutUser = async () => {
        const newUser = await UserService.logoutUser(user);
        if (newUser) {
            setUser(newUser);
            navigate('/login', { replace: true });
        }
    }


    const canEdit = useMemo(() => {return project.id && project.users.find(entry => entry.username === user.username && entry.role > 0)}, [project, user]);

    const changeProject = (proj: Project) => {
        !!project.users.find(entry => entry.username === user.username && entry.role !== 0) && UserControlService.save(); 
        changeActiveProject(proj);
    }

    const getProjectEntries = (projects: Project[], notDeletable?: boolean) => {
        return projects.map((proj: Project) => (
            <div className="topNavDropdownItem" key={proj.id} onClick={() => { changeProject(proj) }} style={proj.id === project.id ? {backgroundColor: "var(--mdb-dark)", color: "white",  fontWeight: "bold"} : {}}>
                {proj.name}
            </div>)
        )
    }

    const projects = projectList.activeProject ? getProjectEntries(projectList.projects.filter(p => p.users.find(entry => entry.username === user.username && entry.role === 2) || p.users.length <= 0)) : [];
    const sharedProjects = projectList.activeProject ? getProjectEntries(projectList.projects.filter(p => p.users.find(entry => entry.username === user.username && entry.role !== 2)), true) : [];


    // toggle static sidebar
    const [staticSidenav, setStaticSidenav] = useState(Settings.sideNav.isStatic);
    
	const toggleStaticSidenav = () => {
        NavEventService.dispatch(NavEvent.TOGGLE_STATIC_SIDENAV, !staticSidenav);
        setStaticSidenav(!staticSidenav);
        Settings.setNavbarStatic(!staticSidenav);
    }

    // toggle task editor preview
    const [graphPreviewEnabled, setGraphPreviewEnabled] = useState(Settings.sideNav.showGraph);
    const toggleGraphPreview = () => {
        TaskEditorEventService.dispatch(TaskEditorEvent.ENABLE_PREVIEW, !graphPreviewEnabled);
        setGraphPreviewEnabled(!graphPreviewEnabled);
        Settings.setNavbarShowGraph(!graphPreviewEnabled);
    }

    // toggle ideabox
    const [ideaBoxEnabled, setIdeaBoxEnabled] = useState(Settings.sideNav.showIdeabox);
    const toggleIdeaBox = () => {
        NavEventService.dispatch(NavEvent.TOGGLE_IDEABOX, !ideaBoxEnabled);
        setIdeaBoxEnabled(!ideaBoxEnabled);
        Settings.setNavbarShowIdeaBox(!ideaBoxEnabled);
    }

    // toggle table glyph sketching (page setting)
    const [tableGlyphSketching, setTableGlyphSketching] = useState(Settings.sideNav.tableGlyphSketching);
    const toggleTableGlyphSketching = () => {
        NavEventService.dispatch(NavEvent.TOGGLE_TABLE_GLYPHSKETCHING, !tableGlyphSketching);
        setTableGlyphSketching(!tableGlyphSketching);
        Settings.setTableGlyphSketching(!tableGlyphSketching);
    }

    // toggle hide glyphs (page setting)
    const [hideGlyphs, setHideGlyphs] = useState(Settings.sideNav.hideGlyphs);
    const toggleHideGlyphs = () => {
        GlyphEventService.dispatch(GlyphEvent.TOGGLE_HIDE_GLYPHS, Settings.sideNav.hideGlyphs);
        setHideGlyphs(!hideGlyphs);
        Settings.setHideGlyphs(!hideGlyphs);
    }

    // dropdown states
    const [openFileMenuDropdown, setOpenFileMenuDropdown] = useState(false);
    const [openDropright, setOpenDropright] = useState(false);
    const [openSettingsDropdown, setOpenSettingsDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event:any) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setOpenFileMenuDropdown(false);
          setOpenSettingsDropdown(false);
        }
      };
  
      if (openFileMenuDropdown || openSettingsDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }
  
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [openFileMenuDropdown, setOpenFileMenuDropdown, openSettingsDropdown, setOpenSettingsDropdown]);


    return (
        <>
            <input type='file' id='konect-file-input' onInput={importProject} />
            <header>
                <MDBNavbar expand='lg' dark bgColor='dark' style={{height: "60px"}}>
                    <div className='konectNavBar'>
                        {/* Only show the following block, if user is logged in */
                        !props.loginView && <MDBNavbarNav className='d-flex gap-2' >
                            <MDBNavbarItem>
                                <div className="position-relative h-100">
                                    <div className="topNavBurger navbar-icon" style={{marginRight: "10px"}} onClick={() => setOpenFileMenuDropdown(true) }>
                                        <MDBIcon fas icon="bars" />
                                    </div>
                                    {openFileMenuDropdown && createPortal(
                                        <div ref={dropdownRef} className="topNavDropdown shadow-5">
                                            <div className="topNavDropdownItem" onClick={() => { props.setShowProjectDialog(true) }}>
                                                <div className="fileMenuItem">
                                                    <MDBIcon fas icon="plus" />
                                                    <span>New</span>
                                                </div>
                                            </div>
                                            <div className="topNavDropdownItem position-relative" onMouseEnter={() => setOpenDropright(true)} onMouseLeave={() => setOpenDropright(false)}>
                                                <div className="d-flex flex-row w-100 align-items-center justify-content-between">
                                                    <div className="fileMenuItem">
                                                        <MDBIcon fas icon="project-diagram" />
                                                        <span>Open</span>
                                                    </div>
                                                    <MDBIcon fas icon="angle-right" className="place-self-end" />
                                                    {openDropright && <div className="topNavDropright shadow-5">
                                                        {projects.length > 0 && <div className="projectMenuTitle">Your Projects</div>}
                                                        {projects}
                                                        {sharedProjects.length > 0 && <div className="projectMenuTitle">Shared Projects</div>}
                                                        {sharedProjects}
                                                    </div>}
                                                </div>
                                            </div>
                                            <div className="topNavDropdownItem" onClick={() => { document.getElementById('konect-file-input')?.click() }}>
                                                <div className="fileMenuItem">
                                                    <MDBIcon fas icon="upload" />
                                                    <span>Import</span>
                                                </div>
                                            </div>
                                            <div className="topNavDropdownItem" onClick={() => { ProjectService.exportProject(project) }}>
                                                <div className="fileMenuItem">
                                                    <MDBIcon fas icon="download" />
                                                    <span>Export</span>
                                                </div>
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                                </div>
                            </MDBNavbarItem>
                            {canEdit ? <div className="d-flex gap-1">
                                <MDBNavbarItem className='d-flex align-items-center'>
                                    <div className={'navbar-icon' + (saveAvailable ? " enabled" : " disabled")}
                                        onClick={save}>
                                        {isSaving ? <i className="fas fa-spinner accept-modal-spinner"></i> : <i className="fas fa-save"></i>}
                                    </div>
                                </MDBNavbarItem>

                                <MDBNavbarItem className='d-flex align-items-center'>
                                    <div className={'navbar-icon' + (undoAvailable ? " enabled" : " disabled")}
                                        onClick={undo}>
                                        <i className="fas fa-undo"></i>
                                    </div>
                                </MDBNavbarItem>

                                <MDBNavbarItem className='d-flex align-items-center'>
                                    <div className={'navbar-icon' + (redoAvailable ? " enabled" : " disabled")}
                                        onClick={redo}>
                                        <i className="fas fa-redo"></i>
                                    </div>
                                </MDBNavbarItem>
                            </div> : <div className='spectator-hint'>SPECTATOR</div>}
                        </MDBNavbarNav>}

                        <MDBNavbarNav className="justify-content-center">
                            <MDBNavbarBrand href='#'>
                                <img
                                    src='./images/KonectValueIcon.png'
                                    height='30'
                                    alt=''
                                    loading='lazy'
                                />
                                KONECT
                            </MDBNavbarBrand>
                        </MDBNavbarNav>

                        {/* Only show the following block, if user is logged in */
                        !props.loginView && <MDBNavbarNav className="justify-content-end d-flex gap-1"> 
                            <MDBNavbarItem>
                                <div className="position-relative h-100">
                                    <div className="navbar-icon" onClick={() => setOpenSettingsDropdown(true) }>
                                        <MDBIcon fas icon="ellipsis-v" />
                                    </div>
                                    {openSettingsDropdown && createPortal(
                                        <div ref={dropdownRef} className="topNavDropdown shadow-5" style={{right: 0}}>
                                            <div className="topNavToggle">
                                                <span>Static Sidebar</span>
                                                <MDBSwitch onChange={toggleStaticSidenav} defaultChecked={Settings.sideNav.isStatic} />
                                            </div>
                                            <div className="topNavToggle">
                                                <span>Sidebar Graph</span>
                                                <MDBSwitch onChange={toggleGraphPreview} defaultChecked={Settings.sideNav.showGraph} />
                                            </div>
                                            <div className="topNavToggle">
                                                <span>Idea Box</span>
                                                <MDBSwitch onChange={toggleIdeaBox} defaultChecked={Settings.sideNav.showIdeabox} />
                                            </div>
                                            <hr style={{margin:"0.25rem 0"}}/>

                                            {(props.navState === NavState.GLYPH_1A || props.navState === NavState.GLYPH_1B) && <div className="topNavToggle">
                                                <span>Table Sketching</span>
                                                <MDBSwitch onChange={toggleTableGlyphSketching} defaultChecked={Settings.sideNav.isStatic} />
                                            </div>}
                                            {props.navState === NavState.GLYPH_2 && <div className="topNavToggle">
                                                <span>Hide Glyphs</span>
                                                <MDBSwitch onChange={toggleHideGlyphs} defaultChecked={Settings.sideNav.isStatic} />
                                            </div>}
                                            {((props.navState === NavState.GLYPH_1A || props.navState === NavState.GLYPH_1B || props.navState === NavState.GLYPH_2)) && <hr style={{margin:"0.25rem 0"}}/>}

                                            {!Settings.useOfflineMode && <div className="topNavDropdownItem" style={{width: "100%"}} onClick={() => setShowUserSettings(true)}>
                                                <div className="fileMenuItem">
                                                    <MDBIcon fas icon="user-cog" />
                                                    <span>User Settings</span>
                                                </div>
                                            </div>}

                                            <div className="topNavDropdownItem" style={{width: "100%"}} onClick={() => logoutUser()}>
                                                <div className="fileMenuItem">
                                                    <MDBIcon fas icon="sign-out-alt" />
                                                    <span>Logout</span>
                                                </div>
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                                </div>
                            </MDBNavbarItem>
                        </MDBNavbarNav>}
                    </div>
                </MDBNavbar>
            </header >
            <UserSettings showState={[showUserSettings, setShowUserSettings]}/>
        </>
    );
}